import { AppConfigService, SidenavLayoutComponent } from '@alfresco/adf-core';
import {
  Component,
  Injector,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { NavigationEnd, Router, NavigationStart } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Directionality } from '@angular/cdk/bidi';
import { Store } from '@ngrx/store';
import { Subject, Observable, noop } from 'rxjs';
import {
  filter,
  takeUntil,
  map,
  withLatestFrom,
  tap,
  switchMap,
  take,
} from 'rxjs/operators';

import { SelectionState } from '@alfresco/adf-extensions';
import { UploadFilesEvent } from '@alfresco/adf-content-services';
import {
  SHELL_APP_SERVICE,
  ShellAppService,
  ShellLayoutComponent,
} from '@alfresco/adf-core/shell';
import { AppService } from '@adw/projects/aca-shared/src/public-api';

import { WkUserPreferencesService } from '../../../../services/wk-user-preferences.service';
import { WkAppConfigService } from '../../../../services/wk-app-config.service';
import { WkNodePermissionService } from '../../../../services/wk-node-permission.service';
import { AppStore } from '../../../../store/states/app.state';
import {
  getAppSelection,
  getCurrentFolder,
  getFeatureFlags,
  isInfoDrawerOpened,
} from '../../../../store/selectors/app.custom-selectors';
import {
  ResetSelectionAction,
  SetInfoDrawerStateAction,
} from '../../../../store/actions/app.custom-actions';
import { WkFileUploadEventService } from '../../../../services/wk-file-upload-event.service';
import { FEATURE_FLAGS } from '../../../../constants';

@Component({
  selector: 'wk-app-layout',
  templateUrl: './wk-app-layout.component.html',
  styleUrls: ['./wk-app-layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: { class: 'app-layout' },
  standalone: false,
})
export class WkAppLayoutComponent
  extends ShellLayoutComponent
  implements OnInit, OnDestroy
{
  // #region Properties (19)

  private readonly _appConfigService: WkAppConfigService;
  private readonly _breakpointObserver: BreakpointObserver;
  private readonly fileuploadEventService: WkFileUploadEventService;
  private readonly hideConditions: string[] = ['/preview/'];
  private readonly minimizeConditions: string[] = ['search'];
  private readonly permission: WkNodePermissionService;
  private readonly _router: Router;
  private readonly store: Store<AppStore>;
  private readonly userPreferenceService: WkUserPreferencesService;
  private readonly appService: AppService;
  public readonly aiKey = FEATURE_FLAGS.ENABLE_APOLLO_AI_FEATURE;
  public canUpload = false;
  public currentFolderId: string;
  public direction: Directionality;
  public expandedSidenav: boolean;
  public hideSidenav = false;
  isSmallScreen$: Observable<boolean>;
  @ViewChild('layout', { static: true })
  public layout: SidenavLayoutComponent;
  public minimizeSidenav = false;
  public node;
  public expyChatSidebarEnabled = false;
  public appConfigAIEnabled: Observable<{ [key: string]: boolean }>;
  onDestroy$: Subject<boolean> = new Subject<boolean>();

  // #endregion Properties (19)

  // #region Constructors (1)

  constructor(injector: Injector) {
    super(
      injector.get<Router>(Router),
      injector.get<AppConfigService>(AppConfigService),
      injector.get<BreakpointObserver>(BreakpointObserver),
      injector.get<ShellAppService>(SHELL_APP_SERVICE),
      0,
      320
    );
    this.fileuploadEventService = injector.get<WkFileUploadEventService>(
      WkFileUploadEventService
    );
    this.store = injector.get<Store<AppStore>>(Store);
    this._router = injector.get<Router>(Router);
    this.permission = injector.get<WkNodePermissionService>(
      WkNodePermissionService
    );
    this.userPreferenceService = injector.get<WkUserPreferencesService>(
      WkUserPreferencesService
    );
    this._appConfigService =
      injector.get<WkAppConfigService>(WkAppConfigService);
    this._breakpointObserver =
      injector.get<BreakpointObserver>(BreakpointObserver);
    this.appService = injector.get<AppService>(AppService);
  }

  // #endregion Constructors (1)

  // #region Public Methods (5)

  public hideMenu(event: Event) {
    if (this.layout.container.isMobileScreenSize) {
      event.preventDefault();
      this.layout.container.toggleMenu();
    }
  }

  public ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  public ngOnInit() {
    this.expyChatSidebarEnabled = this._appConfigService.get(
      'expyChatSidebarEnabled',
      false
    );
    this.isSmallScreen$ = this._breakpointObserver
      .observe(['(max-width: 600px)'])
      .pipe(map((result) => result.matches));
    this.hideSidenav = this.hideConditions.some((el) =>
      this._router.routerState.snapshot.url.includes(el)
    );
    this.minimizeSidenav = this.minimizeConditions.some((el) =>
      this._router.routerState.snapshot.url.includes(el)
    );
    if (!this.minimizeSidenav) {
      this.expandedSidenav = this._getSidenavState();
    } else {
      this.expandedSidenav = false;
    }

    this.store
      .select(getAppSelection)
      .pipe(
        filter(
          (state: SelectionState) =>
            this._router.url.startsWith('/search') &&
            !!(state.file || state.folder || state.library)
        ),
        switchMap(() =>
          this.store.select(isInfoDrawerOpened).pipe(
            filter((opened: boolean) => !opened),
            take(1)
          )
        ),
        tap(() => this.store.dispatch(new SetInfoDrawerStateAction(true))),
        takeUntil(this.onDestroy$)
      )
      .subscribe(noop);

    this.store
      .select(getCurrentFolder)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((node) => {
        this.node = node;
        this.currentFolderId = node ? node.id : null;
        this.canUpload = node && this.permission.check(node, ['create']);
      });

    this._router.events
      .pipe(
        withLatestFrom(this.isSmallScreen$),
        filter(
          ([event, isSmallScreen]) =>
            isSmallScreen && event instanceof NavigationEnd
        ),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => {
        this.layout.container.sidenav.close();
      });
    this.appConfigAIEnabled = this.store.select(getFeatureFlags);
    this.whenNavigationEnd();
    this.resetSelectionAction();
  }

  public onBeginUpload(ev: UploadFilesEvent) {
    this.fileuploadEventService.triggerUploadFilesEvent(ev);
  }

  public onExpanded(state: boolean) {
    if (
      !this.minimizeSidenav &&
      this._appConfigService.get('sideNav.preserveState')
    ) {
      this.userPreferenceService.set('expandedSidenav', state);
    }
  }

  // #endregion Public Methods (5)

  // #region Private Methods (4)

  private _getSidenavState(): boolean {
    const expand = this._appConfigService.get<boolean>(
      'sideNav.expandedSidenav',
      true
    );
    const preserveState = this._appConfigService.get<boolean>(
      'sideNav.preserveState',
      true
    );

    if (preserveState) {
      return (
        this.userPreferenceService.get('expandedSidenav', expand.toString()) ===
        'true'
      );
    }
    return expand;
  }

  private resetSelectionAction(): void {
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => {
        this.store.dispatch(new ResetSelectionAction());
      });
  }

  private _updateState() {
    if (!this.minimizeSidenav && this.layout.isMenuMinimized) {
      this.appService.toggleAppNavBar$.next();
    }
  }

  private whenNavigationEnd(): void {
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.onDestroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.minimizeSidenav = this.minimizeConditions.some((el) =>
          event.urlAfterRedirects.includes(el)
        );
        this.hideSidenav = this.hideConditions.some((el) =>
          event.urlAfterRedirects.includes(el)
        );

        this._updateState();
      });
  }

  // #endregion Private Methods (4)
}
